import type { FetchApi } from '@backstage/frontend-plugin-api';
import type {
  CatalogConfig,
  CriteriaConfig,
  MaturityIndex,
  MaturityItem,
  MaturityQuadrant,
  MaturityTopic,
  ProductMaturity,
  ProductSummary,
} from './types';

// Bounded-concurrency map: caps in-flight requests so a fleet of dozens of products,
// each with dozens of item files, doesn't fire thousands of simultaneous browser/proxy
// requests on a single page load.
async function mapWithConcurrency<T, R>(
  items: T[],
  limit: number,
  fn: (item: T, index: number) => Promise<R>,
): Promise<R[]> {
  const results: R[] = new Array(items.length);
  let cursor = 0;
  async function worker() {
    while (cursor < items.length) {
      const current = cursor++;
      results[current] = await fn(items[current], current);
    }
  }
  await Promise.all(
    Array.from({ length: Math.min(limit, items.length) }, worker),
  );
  return results;
}

// Why mock data ended up being used, so the UI can tell the user which condition applied
// instead of silently swapping in sample data.
export type MockReason = 'no-backend' | 'unauthorized' | 'unavailable';
export type OnMock = (reason: MockReason) => void;

async function fetchOrMock<T>(
  fetchApi: FetchApi,
  url: string,
  mockLoader: () => Promise<T>,
  onMock?: OnMock,
): Promise<T> {
  const res = await fetchApi.fetch(url);
  if (res.status === 503 || res.status === 401) {
    onMock?.(res.status === 401 ? 'unauthorized' : 'unavailable');
    return mockLoader();
  }
  if (!res.ok) throw new Error(`Failed to fetch ${url}: ${res.status}`);
  return res.json();
}

export async function loadIndex(
  fetchApi: FetchApi,
  baseUrl: string,
  onMock?: OnMock,
): Promise<MaturityIndex> {
  if (!baseUrl) {
    onMock?.('no-backend');
    return import('./mocks/index.mock.json').then(
      m => m.default as MaturityIndex,
    );
  }
  return fetchOrMock(
    fetchApi,
    `${baseUrl}/index.json`,
    () =>
      import('./mocks/index.mock.json').then(m => m.default as MaturityIndex),
    onMock,
  );
}

export async function loadCriteriaConfig(
  fetchApi: FetchApi,
  baseUrl: string,
  onMock?: OnMock,
): Promise<CriteriaConfig> {
  if (!baseUrl) {
    onMock?.('no-backend');
    return import('./mocks/criterias.mock.json').then(
      m => m.default as CriteriaConfig,
    );
  }
  return fetchOrMock(
    fetchApi,
    `${baseUrl}/criterias.json`,
    () =>
      import('./mocks/criterias.mock.json').then(
        m => m.default as CriteriaConfig,
      ),
    onMock,
  );
}

export async function loadCatalogConfig(
  fetchApi: FetchApi,
  baseUrl: string,
  onMock?: OnMock,
): Promise<CatalogConfig> {
  if (!baseUrl) {
    onMock?.('no-backend');
    return import('./mocks/catalog.mock.json').then(
      m => m.default as CatalogConfig,
    );
  }
  return fetchOrMock(
    fetchApi,
    `${baseUrl}/catalog.json`,
    () =>
      import('./mocks/catalog.mock.json').then(m => m.default as CatalogConfig),
    onMock,
  );
}

type ItemScoreValue =
  | number
  | null
  | {
      score: number | null;
      evidenceUrl?: string | null;
      evidenceTitle?: string | null;
    };
type ItemScores = Record<string, ItemScoreValue>;

function normaliseEntry(v: ItemScoreValue): {
  score: number | null;
  evidenceUrl: string | null;
  evidenceTitle: string | null;
} {
  if (v === null || typeof v === 'number')
    return { score: v, evidenceUrl: null, evidenceTitle: null };
  return {
    score: v.score ?? null,
    evidenceUrl: v.evidenceUrl ?? null,
    evidenceTitle: v.evidenceTitle ?? null,
  };
}

// How many in-flight item-score requests to allow per product, and how many products to
// load concurrently (the latter is enforced by the caller, see QualityMaturityPage).
const ITEM_FETCH_CONCURRENCY = 6;

async function loadItemScores(
  fetchApi: FetchApi,
  baseUrl: string,
  productId: string,
  slug: string,
  onMock?: OnMock,
): Promise<ItemScores> {
  if (!baseUrl) {
    onMock?.('no-backend');
    const allProducts = await import(`./mocks/items/${slug}.mock.json`).then(
      m => m.default as Record<string, ItemScores>,
    );
    return allProducts[productId] ?? {};
  }
  return fetchOrMock(
    fetchApi,
    `${baseUrl}/products/${productId}/${slug}.json`,
    async () => {
      const allProducts = await import(`./mocks/items/${slug}.mock.json`).then(
        m => m.default as Record<string, ItemScores>,
      );
      return allProducts[productId] ?? {};
    },
    onMock,
  );
}

async function loadManifest(
  fetchApi: FetchApi,
  baseUrl: string,
  productId: string,
  onMock?: OnMock,
): Promise<{ items: string[] }> {
  if (!baseUrl) {
    onMock?.('no-backend');
    const allManifests = await import('./mocks/__manifest.mock.json').then(
      m => m.default as Record<string, string[]>,
    );
    return { items: allManifests[productId] ?? [] };
  }
  return fetchOrMock(
    fetchApi,
    `${baseUrl}/products/${productId}/__manifest.json`,
    async () => {
      const allManifests = await import('./mocks/__manifest.mock.json').then(
        m => m.default as Record<string, string[]>,
      );
      return { items: allManifests[productId] ?? [] };
    },
    onMock,
  );
}

export function buildProductMaturity(
  summary: ProductSummary,
  config: CriteriaConfig,
  assessments: Map<string, ItemScores>,
): ProductMaturity {
  const quadrants: ProductMaturity['quadrants'] = {
    alignment: buildQuadrant(config, 'alignment', assessments),
    coverage: buildQuadrant(config, 'coverage', assessments),
    reliability: buildQuadrant(config, 'reliability', assessments),
    lean: buildQuadrant(config, 'lean', assessments),
  };
  return {
    version: '2.0',
    productId: summary.id,
    productLabel: summary.label,
    solution: summary.solution,
    xp: summary.xp,
    platform: summary.platform,
    assessedAt: summary.assessedAt ?? '',
    assessedBy: summary.assessedBy,
    quadrants,
  };
}

function buildQuadrant(
  config: CriteriaConfig,
  quadKey: string,
  assessments: Map<string, ItemScores>,
): MaturityQuadrant {
  const qConf = config.quadrants[quadKey];
  if (!qConf) {
    return { label: quadKey, weight: 1, topics: {} };
  }
  const topics: Record<string, MaturityTopic> = {};
  for (const [topicKey, topicConf] of Object.entries(qConf.topics)) {
    const items: Record<string, MaturityItem> = {};
    for (const [itemKey, itemConf] of Object.entries(topicConf.items)) {
      const scores = assessments.get(itemConf.slug);
      const criteria: MaturityItem['criteria'] = {};
      for (const [critId, critConf] of Object.entries(itemConf.criteria)) {
        const { score, evidenceUrl, evidenceTitle } = scores
          ? normaliseEntry(scores[critId] ?? null)
          : { score: null, evidenceUrl: null, evidenceTitle: null };
        criteria[critId] = {
          score,
          weight: critConf.weight,
          description: critConf.description,
          technicalDescription: critConf.technicalDescription,
          mandatory: critConf.mandatory ?? false,
          evidenceUrl,
          evidenceTitle,
        };
      }
      items[itemKey] = {
        label: itemConf.label,
        weight: itemConf.weight,
        criteria,
      };
    }
    topics[topicKey] = {
      label: topicConf.label,
      weight: topicConf.weight,
      items,
    };
  }
  return { label: qConf.label, weight: qConf.weight, topics };
}

export async function loadProduct(
  fetchApi: FetchApi,
  baseUrl: string,
  summary: ProductSummary,
  config: CriteriaConfig,
  onMock?: OnMock,
): Promise<ProductMaturity> {
  const manifest = await loadManifest(fetchApi, baseUrl, summary.id, onMock);
  // A single missing/broken item file must not drop the whole product from the radar:
  // treat it as unassessed (empty scores) rather than rejecting the entire load.
  const assessmentEntries = await mapWithConcurrency(
    manifest.items,
    ITEM_FETCH_CONCURRENCY,
    async slug => {
      try {
        const scores = await loadItemScores(
          fetchApi,
          baseUrl,
          summary.id,
          slug,
          onMock,
        );
        return [slug, scores] as [string, ItemScores];
      } catch {
        return [slug, {}] as [string, ItemScores];
      }
    },
  );
  const assessments = new Map(assessmentEntries);
  return buildProductMaturity(summary, config, assessments);
}
